import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Compliance } from '@app/services/compliance.service';
import { interval, of, Subscription } from 'rxjs'; 
import { catchError, switchMap, take, takeWhile, tap } from 'rxjs/operators';

// Mock data kept for reference/testing
const _mockData = {
  "message": "Assessment request processed successfully.",
  "results": [
      {
          "api_result": {
              "match_count": 1,
              "matches": [
                  {
                      "average_percentage": 1.0,
                      "column_scores": {
                          "address": 0.0,
                          "dob": 1.0,
                          "email": 1.0,
                          "full_name": 2.0
                      },
                      "record_data": {
                          "address": "Home S1165 COUNTY ROAD I ALMA WI",
                          "dob": "1957-10-03",
                          "email": "j.quarberg@yahoo.com",
                          "full_name": "Janese Quarberg"
                      },
                      "record_id": 343
                  }
              ],
              "status": "Matched"
          },
          "rule": "Gaming Exclusion List"
      },
      {
          "api_result": {
              "match_count": 1,
              "matches": [
                  {
                      "average_percentage": 1.5,
                      "column_scores": {
                          "dob": 1.0,
                          "full_name": 2.0
                      },
                      "record_data": {
                          "dob": "1957-10-03",
                          "full_name": "Janese Quarberg"
                      },
                      "record_id": 343
                  }
              ],
              "status": "Matched"
          },
          "rule": "ITRAK Exclusion List"
      },
      {
          "api_result": {
              "match_count": 0,
              "matches": [],
              "status": "No Match"
          },
          "rule": "Sanctions"
      }
  ]
};

const createSanctionsParsedResult = (api: string, result: CashCageResult) : CashCageParsedResults => {
  const parsedResults = {
    api: api,
    results: []
  } as CashCageParsedResults;
  if (!result) {
    return parsedResults;
  }
  const add = (match, key: string, parsedResult: CashCageParsedResult, props = []) => {
    
    const parts = key.split('.');
    const value = parts.reduce((acc, part) => acc && acc[part], match);
    const header = parts.map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' - ').replace(/_/g, ' ');
    if (value !== undefined) {
      let actualValue = value;
      if (props.length > 0) {
        actualValue = value.map((item: any) => {
          return props.reduce((acc, prop) => acc && acc[prop], item);
        }).join(', ');
      }
      parsedResult.contents[header] = actualValue;
      parsedResult.headers.push(header);
    }
  };
  (result.matches as SanctionsResultMatch[]).forEach((match) => {
    const parsedResult = {
      headers: [],
      contents: {}
    } as CashCageParsedResult;
    
    add(match, 'first_name', parsedResult);
    add(match, 'last_name', parsedResult);
    add(match.data, 'features.Birthdate', parsedResult);
    add(match.data, 'features.Citizenship Country', parsedResult);
    add(match.data, 'features.Place of Birth', parsedResult);
    add(match.data, 'sanctionsLists', parsedResult, ["name"]);
    add(match.data, 'sanctionsPrograms', parsedResult, ["name"]);
    add(match.data, 'sanctionsTypes', parsedResult, ["name"]);

    parsedResults.results.push(parsedResult);
  })
  return parsedResults;
};
const createParsedResult = (api: string, result: CashCageResult) : CashCageParsedResults => {
  const parsedResults = {
    api: api,
    results: []
  } as CashCageParsedResults;
  if (!result) {
    return parsedResults;
  }
  const add = (match: CashCageResultMatch, key: string, parsedResult: CashCageParsedResult) => {
    
    const parts = key.split('.');
    const value = parts.reduce((acc, part) => acc && acc[part], match);
    const header = parts.map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' - ').replace(/_/g, ' ');
    if (value !== undefined) {
      parsedResult.contents[header] = value;
      parsedResult.headers.push(header);
    }
  };
  (result.matches as CashCageResultMatch[]).forEach(match => {
    const parsedResult = {
      headers: [],
      contents: {}
    } as CashCageParsedResult;
    add(match, 'average_percentage', parsedResult);
    add(match, 'record_id', parsedResult);
    ['dob', 'full_name', 'email', 'address'].forEach(key => {
      add(match, `column_scores.${key}`, parsedResult);
      add(match, `record_data.${key}`, parsedResult);
    });
    parsedResults.results.push(parsedResult);
  })
  return parsedResults;
};

interface SanctionsResultMatch {
  birthdate: string;
  data: {
    addresses: {
      list: {
        address1?: string;
        city?: string;
        country: string;
      }[];
    }[];
    features: Record<string, string[]>;
    legalAuthorities: any[]; // empty array, type unknown
    names: {
      isLowQuality: boolean;
      items: {
        formattedFirstName: string;
        formattedFullName: string;
        formattedLastName: string;
        nameparts: {
          "First Name": string;
          "Last Name": string;
        };
        script: string;
      }[];
      type: string;
    }[];
    sanctionsLists: {
      datePublished: string;
      id: string;
      name: string;
      refId: string;
    }[];
    sanctionsPrograms: {
      id: string;
      name: string;
      refId: string;
    }[];
    sanctionsTypes: {
      id: string;
      name: string;
      refId: string;
    }[];
  };
  first_name: string;
  id: number;
  last_name: string;
}
interface CashCageResultMatch {
  average_percentage: number;
  column_scores: {
      dob?: number;
      full_name?: number;
      [key: string]: number | undefined; // Allow additional score fields
  };
  record_data: {
      dob: string;
      full_name: string;
      [key: string]: string; // Allow additional record data fields
  };
  record_id: number;
}
interface CashCageParsedResult {
  headers: string[];
  contents: object;
}
interface CashCageParsedResults {
  api: string;
  results: CashCageParsedResult[];
}
interface CashCageResults {
  api_result: CashCageResult;
  rule: string;
}
interface CashCageResult {
  match_count: number;
  matches: CashCageResultMatch[] | SanctionsResultMatch[];
  status: string;
}
interface CashCageResponse {
  message: string;
  results: CashCageResults[];
}

@Component({
  standalone: false,
  selector: 'app-security-compliance-layout',
  templateUrl: './security-compliance-layout.component.html',
  styleUrls: ['./security-compliance-layout.component.css'],
})
export class SecurityComplianceLayoutComponent implements OnInit {
  pageTitle = '';
  cashCageForm: FormGroup;
  scanning = false;
  selfie = false;
  private pollingSubscription: Subscription | null = null;
  isLoading = false;
  isVerifyingSSN = false;
  isFindingSSN = false;
  activeTab = '';
  imgFront: string;
  imgBack: string;
  isScanningSide: string;
  req_id_back: string;
  req_id_front: string;
  operation_id: string;
  isScaningLoad = false;
  cashCageDetails: any
  condents:any
  selectionApi:any
  isOver21 = false;
  ageVerificationStatus: 'pending' | 'verified' | 'failed' = 'pending';
  ssnVerificationStatus: 'pending' | 'verified' | 'failed' = 'pending';

  leftFormFields = [
    {
      label: 'Phone Number',
      controlName: 'phone',
      type: 'text',
      placeholder: 'Phone Number',
      maxLength: 12,
      pattern: '\\d{3}-\\d{3}-\\d{4}',
    },
    { label: 'Email', controlName: 'email', type: 'email', placeholder: 'Email' },
    { label: 'Address', controlName: 'address', type: 'text', placeholder: 'Street, City, State' },
    { label: 'Country', controlName: 'country', type: 'text', placeholder: 'Country' },
  ];

  rightFormFields = [
    { label: 'First Name', controlName: 'firstName', type: 'text', placeholder: 'First Name' },
    { label: 'Middle Name', controlName: 'middleName', type: 'text', placeholder: 'Middle Name (Optional)' },
    { label: 'Last Name', controlName: 'lastName', type: 'text', placeholder: 'Last Name' },
    {
      label: 'Date of Birth',
      controlName: 'dob',
      type: 'text',
      placeholder: 'MM/DD/YYYY',
      maxLength: 10,
      pattern: '\\d{2}/\\d{2}/\\d{4}',
    },
    {
      label: 'SSN',
      controlName: 'ssn',
      type: 'text',
      placeholder: 'SSN',
      maxLength: 11
    },
  ];

  constructor(private scan: Compliance, private route: ActivatedRoute, private fb: FormBuilder) {}

  ngOnInit() {
    this.pageTitle = 'Security Compliance';
    this.cashCageForm = this.fb.group({
      firstName: ['', Validators.required], 
      middleName: [''],
      lastName: ['', Validators.required],
      dob: ['', [Validators.required, Validators.pattern(/^\d{2}\/\d{2}\/\d{4}$/)]], 
      phone: ['', [Validators.required, Validators.pattern(/^\+?[0-9]*$/)]], 
      address: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      country: ['', Validators.required],
      ssn: ['', [Validators.required]], 
    });

  }

  capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
  phoneValidator(control: FormControl) {
    const phoneRegex = /^\+?[0-9]{1,12}$/;
    return phoneRegex.test(control.value) ? null : { invalidPhone: true };
  }
  
  ssnValidator(control: FormControl) {
    const ssnRegex = /^\d{9}$/;
    return ssnRegex.test(control.value) ? null : { invalidSsn: true };
  }
  
  dobValidator(control: FormControl) {
    return this.isValidDOB(control.value) ? null : { invalidDob: true };
  }
  
  validateNumberInput(event: KeyboardEvent, field: string) {
    const input = event.target as HTMLInputElement;
    let value = input.value;
  
    if (field === 'phone') {
      value = value.replace(/[^0-9+]/g, '');
      if (value.startsWith('+')) {
        value = '+' + value.slice(1).replace(/\+/g, ''); 
      } else {
        value = value.replace(/\+/g, ''); 
      }
      value = value.slice(0, 12); 
    }
  
    if (field === 'ssn') {
      value = value.replace(/\D/g, '').slice(0, 9);
    }
  
    if (field === 'dob') {
      value = value.replace(/[^0-9/]/g, '');
      if (value.length > 2 && value[2] !== '/') value = value.slice(0, 2) + '/' + value.slice(2);
      if (value.length > 5 && value[5] !== '/') value = value.slice(0, 5) + '/' + value.slice(5);
      value = value.slice(0, 10); 
  
      if (!this.isValidDOB(value)) {
        this.cashCageForm.controls['dob'].setErrors({ invalidDob: true });
      } else {
        this.cashCageForm.controls['dob'].setErrors(null);
      }
    }
  
    input.value = value;
    this.cashCageForm.controls[field].setValue(value);
  }
  
  isValidDOB(dob: string): boolean {
    const dobRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/;
    if (!dobRegex.test(dob)) return false;
    const [month, day, year] = dob.split('/').map(Number);
    const enteredDate = new Date(year, month - 1, day);
    return (
      enteredDate.getFullYear() === year &&
      enteredDate.getMonth() === month - 1 &&
      enteredDate.getDate() === day
    );
  }

  preventPaste(event: ClipboardEvent, field: string) {
    if (['phone', 'dob'].includes(field)) {
      event.preventDefault(); 
    }
    
  }
  clearForm() {
    this.cashCageForm.reset(); 
    this.cashCageForm.setValue({
      firstName: '',
      middleName: '',
      lastName: '',
      dob: '',
      phone: '',
      address: '',
      email: '',
      country: '',
      ssn: '',
    });
  }

  jackpotAction() {
    console.log('Jackpot Action triggered.');
  }

  Documents() {
    this.scanning = true;
  }

  scanningID(scanningSide: string) {
    this.isScaningLoad = true;
    this.isScanningSide = scanningSide;
    this.scan.postScanning(scanningSide).subscribe(
      (response) => {
        if (response.request_id) {
          this.getImages(scanningSide, response.request_id);
        }
      },
      (error) => {
        console.log(error);
        this.isScaningLoad = !true;
      },
    );
  }

  closeScanning() {
    this.scanning = false;
    this.stopPolling();
    this.req_id_front = ""
    this.req_id_back = ""
    this.imgBack = ""
    this.imgFront = ""
  }

  // Polling methods
  getImages(imageSide: string, requestId: any) {
    if (this.pollingSubscription) {
      this.stopPolling();
    }
    let pollingCount = 0;
    this.pollingSubscription = interval(5000)
      .pipe(
        take(6),
        switchMap(() => {
          pollingCount++;
          return this.scan.getScanning(imageSide, requestId).pipe(
            catchError((error) => {
              console.error('Polling Error:', error);
              return of(null);
            }),
          );
        }),
        takeWhile((response) => !this.isScanComplete(response)),
        tap((response) => {
          if (response.data) {
            if (imageSide === 'scan_front') {
              const base64 = `data:image/png;base64,${response.data}`;
              this.imgFront = base64;
              this.req_id_front = requestId;
            } else if (imageSide === 'scan_back') {
              const base64 = `data:image/png;base64,${response.data}`;
              this.imgBack = base64;
              this.req_id_back = requestId;
            }
            this.stopPolling();
          } else if (pollingCount === 6) {
            console.error('Error: Maximum polling attempts reached.');
            this.stopPolling();
          }
        }),
      )
      .subscribe(
        (response) => console.log('Polling Response:', response),
        (error) => console.error('Final Error:', error),
      );
  }

  updatesImgs() {
    this.isLoading = true;
    this.scan.uploadImgs(this.req_id_front, this.req_id_back).subscribe(
      (response) => {
        this.isLoading = !true;
        this.closeScanning()
        this.operation_id = response.operation_id;
        this.callOperationId(response.operation_id);
      },
      (error) => {
        this.isLoading = !true;
        console.error('Upload Error:', error);
      },
    );
  }

  private isScanComplete(response: any): boolean {
    return response && response.status === 'COMPLETED';
  }

  private stopPolling() {
    this.isScaningLoad = !true;
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  private callOperationId(operation_id:string) {
    this.scan.getProofResults(operation_id).subscribe(
      (response) => {
        const cashcageCustomerDetails = response.response_data.sections.filter((i) => i.title === "Document Details")[0].contents[0]
        if (cashcageCustomerDetails) {
          this.cashCageForm.patchValue({
            firstName: cashcageCustomerDetails.NameOfHolder.split(' ')[1] || '',
            lastName: cashcageCustomerDetails.NameOfHolder.split(' ')[0] || '',
            dob: cashcageCustomerDetails.DateOfBirth || '',
            address: cashcageCustomerDetails.Address || '',
            country: cashcageCustomerDetails.IssuingStateName || '',
          });
        }
        this.Screen()
      },
      (error) => {
        console.log(error);
      },
    );
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'NO MATCH':
        return 'badge-success';
      case 'MATCHED':
        return 'badge-danger';
      case 'SKIPPED':
        return 'badge-info';
      case 'ERROR':
        return 'badge-warning';
      default:
        return '';
    }
  }

  Screen(){
    this.isLoading = true
    const cashCageData = {
      firstname: this.cashCageForm.value.firstName,
      lastname: this.cashCageForm.value.lastName,
      date_of_birth: this.cashCageForm.value.dob,
      address_line_1: this.cashCageForm.value.address,
      state: this.cashCageForm.value.country,
      email: this.cashCageForm.value.email,
      isPatron: true
    }

    this.cashCageDetails = cashCageData;
    // console.log('processing mock data');
    // this.processCashCageResponse(mockData);
    this.scan.cashCage(cashCageData).subscribe(this.processCashCageResponse.bind(this), ()=>{
      this.isLoading = false
    })
  }

  processCashCageResponse(response: CashCageResponse) {
      const title = ["Gaming", "Sanctions", "ITRAK"];

      if (response === null) {
        this.isLoading = false
        return;
      }
      const data = title.map(apiName => {
        const result = response.results.find((item: CashCageResults) => item.rule.includes(apiName));
        const apiResult = result.api_result;
        let parsedResult = {} as CashCageParsedResults;
        if (apiName === "Sanctions") {
          parsedResult = createSanctionsParsedResult(result.rule, result.api_result);
        } else {
          parsedResult = createParsedResult(result.rule, result.api_result)
        }
        return {
          "rule": result.rule,
          "status": apiResult ? apiResult.status.toUpperCase() : 'No results',
          "result": parsedResult
        }
      });
      this.condents = data
      this.isLoading = false
  };

  tabClicked(item: {result: CashCageParsedResult, rule: string}) {
    this.setActiveTab(item.rule);
    this.openApiData(item.result);
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  isActiveTab(tab: string): boolean {
    return this.activeTab === tab;
  }

  openApiData(result: CashCageParsedResult){
    this.selectionApi = result
  }

  verifyAge() {
    const dob = this.cashCageForm.get('dob').value;
    if (!dob) {
      this.ageVerificationStatus = 'failed';
      return;
    }

    const [month, day, year] = dob.split('/').map(Number);
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    this.isOver21 = age >= 21;
    this.ageVerificationStatus = this.isOver21 ? 'verified' : 'failed';
  }

  verifySSN() {
    this.isVerifyingSSN = true;
    const ssnControl = this.cashCageForm.get('ssn');
    const ssn = ssnControl ? this.cashCageForm.get('ssn').value : null;

    const ssnRegex = /^(?!000|666|9\d{2})\d{3}[-]?(?!00)\d{2}[-]?(?!0000)\d{4}$/;
    if (!ssnRegex.test(ssn)) {
      this.ssnVerificationStatus = 'failed';
      this.isVerifyingSSN = false;
      return;
    }

    this.scan.verifySSN(ssn).subscribe(response => {
      if (response && response.valid) {
        this.ssnVerificationStatus = 'verified';
      } else {
        this.ssnVerificationStatus = 'failed';
      }
      this.isVerifyingSSN = false
    }, (error)=>{
      console.error('Error verifying SSN:', error);
      this.ssnVerificationStatus = 'failed';
      this.isVerifyingSSN = false
    })
  }

  getAgeVerificationClass(): string {
    switch (this.ageVerificationStatus) {
      case 'verified':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  }

  getSSNVerificationClass(): string {
    switch (this.ssnVerificationStatus) {
      case 'verified':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  }

  findSSN() {
    const firstName = this.cashCageForm.get('firstName').value;
    const lastName = this.cashCageForm.get('lastName').value;
    const dob = this.cashCageForm.get('dob').value;
    const middleName = this.cashCageForm.get('middleName').value;
    const address = this.cashCageForm.get('address').value;

    if (!firstName || !lastName || !dob) {
      alert('Please fill in First Name, Last Name, and Date of Birth before finding SSN.');
      return;
    }

    this.isFindingSSN = true;

    const data = {
      first_name: firstName,
      last_name: lastName,
      dob: dob,
      middle_name: middleName || undefined,
      address_street: address || undefined
    };

    this.scan.findSSN(data).subscribe(
      response => {
        this.isFindingSSN = false;
        if (response && response.found) {
          this.cashCageForm.patchValue({ ssn: response.ssn });
          console.log('SSN found and populated');
        } else {
          alert('SSN not found. Please verify the information provided.');
        }
      },
      error => {
        this.isFindingSSN = false;
        console.error('Error finding SSN:', error);
        alert('An error occurred while finding SSN. Please try again.');
      }
    );
  }
}